import { useCallback, useEffect, useState } from 'react';
import { api, ApiRequestError } from '../lib/api.js';
import { absoluteTime, relativeAge } from '../lib/format.js';

/**
 * The audit log.
 *
 * New screen. `docs/DESIGN_HANDOFF.md` §5.6: the audit log is the record of
 * everything the system and the operator did, and there was no way to read it
 * without SSH and SQL. "What happened last night" is a question this product
 * should answer in one click.
 *
 * Actor tags are deliberately desaturated. Who did something is not a risk fact,
 * and colouring actors from the verdict palette would put "a worker did this" in
 * the same visual language as "this token is dangerous". Only rows that represent
 * a refusal or failure are tinted, because those are the ones worth finding when
 * scanning.
 */

const ACTORS = [
  ['', 'All'],
  ['user', 'Operator'],
  ['telegram', 'Telegram'],
  ['openclaw', 'Agent'],
  ['worker', 'Worker'],
  ['system', 'System'],
] as const;

interface AuditEntry {
  id: string;
  actor_type: string;
  actor_id: string | null;
  event_type: string;
  entity_type: string | null;
  entity_id: string | null;
  metadata: unknown;
  is_failure: boolean;
  /** Which kind — 'reverted', 'refused', 'failed'. Never flattened to one word. */
  failure_kind: string | null;
  created_at: string;
}

interface AuditResponse {
  entries: AuditEntry[];
  next_before: string | null;
}

/** `trade.execution.refused` reads better as `trade · execution · refused`. */
function humanEvent(eventType: string): string {
  return eventType.split('.').join(' · ');
}

export function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actor, setActor] = useState<string>('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '60' });
      if (actor) params.set('actor_type', actor);
      const result = await api.get<AuditResponse>(`/audit?${params.toString()}`);
      setEntries(result.entries);
      setNextBefore(result.next_before);
    } catch (err) {
      if (!(err instanceof ApiRequestError && err.isUnauthorized)) {
        setError('Could not load the audit log.');
      }
    } finally {
      setLoading(false);
    }
  }, [actor]);

  useEffect(() => {
    void load();
  }, [load]);

  async function loadMore() {
    if (!nextBefore) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams({ limit: '60', before: nextBefore });
      if (actor) params.set('actor_type', actor);
      const result = await api.get<AuditResponse>(`/audit?${params.toString()}`);
      // Deduped by id, matching the pairs feed: the log grows at the head while
      // it is being paged through.
      setEntries((current) => {
        const seen = new Set(current.map((entry) => entry.id));
        return [...current, ...result.entries.filter((entry) => !seen.has(entry.id))];
      });
      setNextBefore(result.next_before);
    } catch {
      setError('Could not load more entries.');
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div className="stack">
      <div className="screen-head">
        <h1 className="screen-title">Audit log</h1>
        <span className="label label-dim">append-only · read-only</span>
      </div>

      <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--s-2)' }}>
        {ACTORS.map(([value, label]) => (
          <button
            key={value || 'all'}
            type="button"
            className="actor"
            data-actor={value || undefined}
            aria-pressed={actor === value}
            onClick={() => setActor(value)}
            style={{
              cursor: 'pointer',
              borderColor: actor === value ? 'var(--border-strong)' : undefined,
              background: actor === value ? 'var(--bg-raised)' : undefined,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {error && (
        <p className="notice" data-tone="alarm" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <p className="notice">Loading…</p>
      ) : entries.length === 0 ? (
        <div className="empty">
          <div className="empty-mark" aria-hidden="true" />
          <p className="empty-title">Nothing recorded yet</p>
          <p className="empty-body">
            Security and trading events land here as they happen — logins,
            approvals, refusals, every trade. On a fresh deployment this is
            genuinely empty rather than broken.
          </p>
        </div>
      ) : (
        <>
          <div className="rows">
            {entries.map((entry) => (
              <div key={entry.id}>
                <div className="audit-row" data-failure={entry.is_failure}>
                  <span className="data-xs muted" title={absoluteTime(entry.created_at)}>
                    {relativeAge(entry.created_at)} ago
                  </span>

                  <span className="actor" data-actor={entry.actor_type}>
                    {entry.actor_type}
                  </span>

                  <span className="row" style={{ gap: 'var(--s-2)', flexWrap: 'wrap' }}>
                    <span className="data-sm">{humanEvent(entry.event_type)}</span>
                    {entry.is_failure && (
                      // The event's own word, not a blanket "refused". A
                      // reverted transaction spent gas; a refused one did not.
                      <span className="chip" data-tone="alarm">
                        {entry.failure_kind ?? 'failed'}
                      </span>
                    )}
                    {entry.metadata !== null && (
                      <button
                        className="btn btn-mini"
                        type="button"
                        onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
                        aria-expanded={expanded === entry.id}
                      >
                        {expanded === entry.id ? 'Hide' : 'Detail'}
                      </button>
                    )}
                  </span>
                </div>

                {expanded === entry.id && (
                  <div style={{ padding: '0 var(--s-4) var(--s-3)', background: 'var(--bg-inset)' }}>
                    {/* Stored metadata, verbatim. It was redacted on the way in
                        by key name and by value shape, so what is here is what
                        was safe to keep. */}
                    <pre
                      className="data-xs muted"
                      style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                    >
                      {JSON.stringify(entry.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>

          {nextBefore && (
            <button
              className="btn"
              type="button"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              style={{ alignSelf: 'center' }}
            >
              {loadingMore ? 'Loading…' : 'Load older'}
            </button>
          )}
        </>
      )}
    </div>
  );
}
